import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Plus, ExternalLink, Beaker, FileText, Server, Globe2, RefreshCw, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// AuthContext ustawia axios.defaults.headers.common globalnie — wystarczy zwykły axios.get
const HEADERS = () => ({});

const PLATFORM_BADGES = {
  emergent: { label: "Emergent", color: "bg-purple-100 text-purple-700" },
  wordpress: { label: "WordPress", color: "bg-blue-100 text-blue-700" },
  drupal: { label: "Drupal (planowane)", color: "bg-slate-100 text-slate-500" },
  joomla: { label: "Joomla (planowane)", color: "bg-slate-100 text-slate-500" },
  umbraco: { label: "Umbraco (planowane)", color: "bg-slate-100 text-slate-500" },
};

export default function AdminApiIntegrations() {
  const [sites, setSites] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState({});
  const [tab, setTab] = useState("active"); // active | pending | add

  useEffect(() => {
    document.title = "API Integracje — Admin | Epidemos";
    refresh();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/sites`, { headers: HEADERS() }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/v1/sandbox/runs?limit=30`).catch(() => ({ data: { runs: [] } })),
      ]);
      setSites(Array.isArray(s.data) ? s.data : (s.data.sites || []));
      const allRuns = p.data.runs || [];
      // Pending = ready_for_production ale jeszcze brak w sites lub awaiting approval
      const knownUrls = new Set((Array.isArray(s.data) ? s.data : (s.data.sites || [])).map(x => (x.url || "").replace(/\/+$/, "")));
      setPending(allRuns.filter(r => r.ready_for_production && !knownUrls.has((r.agent_url || "").replace(/\/+$/, ""))));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (siteId) => {
    setTesting({ ...testing, [siteId]: "running" });
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/sites/${siteId}/test-connection`,
        {}, { headers: HEADERS() });
      setTesting({ ...testing, [siteId]: r.data.ok ? "ok" : "fail" });
      setTimeout(() => setTesting(prev => { const c = { ...prev }; delete c[siteId]; return c; }), 5000);
    } catch (e) {
      setTesting({ ...testing, [siteId]: "fail" });
    }
  };

  return (
    <div className="p-6 max-w-none mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">API Integracje portali</h1>
        <p className="text-slate-600 text-sm mt-1">Zarządzanie portalami satelitarnymi podpiętymi pod agenta epidemos. Standard: Publisher API v2.</p>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Link to="/api-docs" className="p-4 bg-white border border-slate-200 rounded-xl hover:border-blue-400 hover:shadow-md transition group">
          <div className="flex items-center gap-3 mb-1">
            <FileText className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-slate-900 group-hover:text-blue-700">Dokumentacja v2</h3>
          </div>
          <p className="text-xs text-slate-500">Pełna specyfikacja standardu do udostępnienia wydawcom</p>
        </Link>
        <Link to="/wydawcy-testy" className="p-4 bg-white border border-slate-200 rounded-xl hover:border-purple-400 hover:shadow-md transition group">
          <div className="flex items-center gap-3 mb-1">
            <Beaker className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-slate-900 group-hover:text-purple-700">Sandbox publiczny</h3>
          </div>
          <p className="text-xs text-slate-500">Wydawca testuje swoje API przed wysłaniem URL</p>
        </Link>
        <button onClick={refresh} className="p-4 bg-white border border-slate-200 rounded-xl hover:border-slate-400 hover:shadow-md transition text-left">
          <div className="flex items-center gap-3 mb-1">
            <RefreshCw className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Odśwież dane</h3>
          </div>
          <p className="text-xs text-slate-500">Pobierz aktualną listę portali i pending requests</p>
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 flex gap-1 px-2 pt-2">
          {[
            { id: "active", label: `Aktywne portale (${sites.length})`, icon: Server },
            { id: "pending", label: `Do moderacji (${pending.length})`, icon: AlertCircle },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition flex items-center gap-2 ${
                      tab === t.id ? "bg-slate-100 text-slate-900 border-b-2 border-blue-500"
                                   : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Active sites */}
        {tab === "active" && (
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Ładowanie…</div>
            ) : sites.length === 0 ? (
              <div className="p-8 text-center text-slate-500">Brak skonfigurowanych portali</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
                  <tr>
                    <th className="text-left p-3">Portal</th>
                    <th className="text-left p-3">Platforma</th>
                    <th className="text-left p-3">API v</th>
                    <th className="text-left p-3">Posty</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {sites.map(s => {
                    const platform = s.site_type === "emergent" ? "emergent" : (s.site_type === "wordpress" ? "wordpress" : s.publisher_platform || "emergent");
                    const badge = PLATFORM_BADGES[platform] || PLATFORM_BADGES.emergent;
                    const apiV = s.api_version || (s.site_type === "emergent" ? "v1" : "wp-native");
                    return (
                      <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="p-3">
                          <div className="font-medium text-slate-900">{s.name || s.url}</div>
                          <a href={s.url} target="_blank" rel="noopener" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
                            {s.url} <ExternalLink className="w-3 h-3" />
                          </a>
                        </td>
                        <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>{badge.label}</span></td>
                        <td className="p-3"><code className="text-xs">{apiV}</code></td>
                        <td className="p-3 text-slate-700">{s.posts_count || 0}</td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                            s.status === "active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${s.status === "active" ? "bg-green-500" : "bg-slate-400"}`}></span>
                            {s.status || "?"}
                          </span>
                        </td>
                        <td className="p-3">
                          <button onClick={() => testConnection(s.id)}
                                  disabled={testing[s.id] === "running"}
                                  className="px-3 py-1.5 text-xs font-medium bg-blue-50 hover:bg-blue-100 text-blue-700 rounded border border-blue-200 transition inline-flex items-center gap-1.5">
                            {testing[s.id] === "running" && <Loader2 className="w-3 h-3 animate-spin" />}
                            {testing[s.id] === "ok" && <CheckCircle2 className="w-3 h-3 text-green-600" />}
                            {testing[s.id] === "fail" && <XCircle className="w-3 h-3 text-red-600" />}
                            {!testing[s.id] && <Globe2 className="w-3 h-3" />}
                            Test połączenia
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Pending */}
        {tab === "pending" && (
          <div className="p-4">
            {pending.length === 0 ? (
              <div className="p-8 text-center text-slate-500">Brak zgłoszeń od wydawców czekających na akceptację</div>
            ) : (
              <div className="space-y-3">
                {pending.map(r => (
                  <div key={r.id} className="p-4 border border-amber-200 bg-amber-50/30 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold text-slate-900">{r.agent_url}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          Run {r.id?.substring(0, 8)} · score {r.score}% · {r.hard_pass}/{r.hard_total} hard pass
                        </div>
                        <div className="text-xs text-slate-500">Test wykonany: {r.created_at?.substring(0, 16)}</div>
                      </div>
                      <div className="flex gap-2">
                        <a href={`/wydawcy-testy?run=${r.id}`} className="px-3 py-1.5 text-xs bg-white border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1">
                          Zobacz raport <ExternalLink className="w-3 h-3" />
                        </a>
                        <button className="px-3 py-1.5 text-xs bg-green-600 hover:bg-green-700 text-white rounded inline-flex items-center gap-1">
                          <Plus className="w-3 h-3" /> Dodaj jako portal
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
        <strong>Workflow podpinania nowego portalu:</strong>
        <ol className="list-decimal pl-5 mt-2 space-y-1 text-blue-800">
          <li>Wydawca otrzymuje URL dokumentacji: <Link to="/api-docs" className="underline">/api-docs</Link></li>
          <li>Implementuje 6 endpointów wg standardu Publisher API v2 (Emergent / WordPress / inny)</li>
          <li>Testuje na <Link to="/wydawcy-testy" className="underline">/wydawcy-testy</Link> — wpisuje URL + klucz</li>
          <li>Po wyniku <code>READY FOR PRODUCTION</code> pojawia się tu w "Do moderacji"</li>
          <li>Klikasz "Dodaj jako portal" → portal trafia do listy aktywnych, agent zaczyna publikować w ciągu 24h</li>
        </ol>
      </div>
    </div>
  );
}
